/**
 * Subscription Health Check Service
 *
 * Verifies and auto-heals webhook and WebSocket subscriptions for imported devices.
 * Ensures Layer 1 (UltronCloud → Turing) and Layer 2 (Turing → Web App) are healthy.
 */

import { ultronTuringApi } from './ultronTuringApi';
import { getImportedDevices } from './userPreferencesApi';
import {
  getWebSocketEnabledDevicesFromFirestore,
  addWebSocketEnabledDevice,
  removeWebSocketEnabledDevice
} from './deviceSubscriptionManager';
import { logger } from '../utils/logger';

// ============================================================
// Type Definitions
// ============================================================

export interface SubscriptionHealth {
  deviceSN: string;
  webhook: {
    subscribed: boolean;
    needsHealing: boolean;
  };
  websocket: {
    subscribed: boolean;
    needsHealing: boolean;
  };
  overall: 'healthy' | 'warning' | 'error';
}

export interface HealthCheckResult {
  timestamp: Date;
  totalDevices: number;
  healthy: number;
  warnings: number;
  errors: number;
  details: SubscriptionHealth[];
  autoHealAttempted: boolean;
  autoHealSuccess: number;
  autoHealFailed: number;
}

// ============================================================
// Layer 1: Webhook Subscription Health Check
// ============================================================

/**
 * Verify webhook subscriptions (UltronCloud → Turing Server)
 * Compares imported devices with actual webhook subscriptions
 *
 * @returns Array of device SNs that need webhook subscription
 */
export async function verifyWebhookSubscriptions(): Promise<{
  subscribed: string[];
  missing: string[];
  orphaned: string[];
}> {
  try {
    logger.log('🔍 [Health Check] Verifying webhook subscriptions (Layer 1)...');

    // Step 1: Get all imported devices from Firestore (intelliSpace only - legacy boards removed)
    const expectedSNs = new Set<string>();

    const boardDevices = await getImportedDevices('intelliSpace');
    // ✅ Fix: Use fallback pattern since migration may have removed 'sn' field
    boardDevices.forEach(device => {
      const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId;
      if (deviceSN) {
        expectedSNs.add(deviceSN);
      }
    });

    logger.log(`📦 [Health Check] Expected devices (imported): ${expectedSNs.size}`);

    // Step 2: Get actual webhook subscriptions from Turing Server
    const subscriptionResult = await ultronTuringApi.querySubscriptions();
    const actualSNs = new Set<string>((subscriptionResult.devices as string[]) || []);

    logger.log(`📡 [Health Check] Actual subscriptions (webhook): ${actualSNs.size}`);

    // Step 3: Find discrepancies
    const missing = Array.from(expectedSNs).filter(sn => !actualSNs.has(sn));
    const orphaned = Array.from(actualSNs).filter((sn: string) => !expectedSNs.has(sn));
    const subscribed = Array.from(expectedSNs).filter(sn => actualSNs.has(sn));

    logger.log(`✅ [Health Check] Webhook status: ${subscribed.length} OK, ${missing.length} missing, ${orphaned.length} orphaned`);

    return { subscribed, missing, orphaned };

  } catch (error) {
    logger.error('❌ [Health Check] Failed to verify webhook subscriptions:', error);
    throw error;
  }
}

// ============================================================
// Layer 2: WebSocket Subscription Health Check
// ============================================================

/**
 * Verify WebSocket subscriptions (Turing Server → Web App)
 * Compares imported devices with WebSocket enabled devices
 *
 * @returns Array of device SNs that need WebSocket subscription
 */
export async function verifyWebSocketSubscriptions(): Promise<{
  subscribed: string[];
  missing: string[];
  extra: string[];
}> {
  try {
    logger.log('🔍 [Health Check] Verifying WebSocket subscriptions (Layer 2)...');

    // Step 1: Get all imported devices (intelliSpace only - legacy boards removed)
    const importedSNs = new Set<string>();

    const boardDevices = await getImportedDevices('intelliSpace');
    // ✅ Fix: Use fallback pattern since migration may have removed 'sn' field
    boardDevices.forEach(device => {
      const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId;
      if (deviceSN) {
        importedSNs.add(deviceSN);
      }
    });

    // Step 2: Get actual WebSocket subscriptions from Firestore
    const wsEnabledDevices = await getWebSocketEnabledDevicesFromFirestore();
    const wsEnabledSNs = new Set(wsEnabledDevices);

    logger.log(`📡 [Health Check] WebSocket enabled: ${wsEnabledSNs.size} devices`);

    // Step 3: Find devices enabled for WebSocket but not imported (shouldn't exist)
    const extra = Array.from(wsEnabledSNs).filter(sn => !importedSNs.has(sn));
    const subscribed = Array.from(wsEnabledSNs).filter(sn => importedSNs.has(sn));

    // Missing = devices that could have WebSocket but don't (not an error, user choice)
    const missing: string[] = [];

    logger.log(`✅ [Health Check] WebSocket status: ${subscribed.length} subscribed, ${extra.length} extra`);

    return { subscribed, missing, extra };

  } catch (error) {
    logger.error('❌ [Health Check] Failed to verify WebSocket subscriptions:', error);
    throw error;
  }
}

// ============================================================
// Auto-Healing Functions
// ============================================================

/**
 * Auto-heal subscription issues
 * Attempts to fix missing webhook/WebSocket subscriptions
 *
 * @param webhookMissing Devices missing webhook subscription
 * @param websocketExtra Devices with extra WebSocket subscription
 * @returns Number of successfully healed subscriptions
 */
export async function autoHealSubscriptions(
  webhookMissing: string[],
  websocketExtra: string[]
): Promise<{ success: number; failed: number }> {
  let success = 0;
  let failed = 0;

  try {
    logger.log('🔧 [Auto-Heal] Starting auto-heal process...');

    // Fix Layer 1: Subscribe missing webhooks
    if (webhookMissing.length > 0) {
      logger.log(`🔧 [Auto-Heal] Subscribing ${webhookMissing.length} missing webhooks...`);
      try {
        await ultronTuringApi.subscribeDevices(webhookMissing);
        success += webhookMissing.length;
        logger.log(`✅ [Auto-Heal] Subscribed ${webhookMissing.length} devices to webhook`);
      } catch (error) {
        logger.error('❌ [Auto-Heal] Failed to subscribe webhooks:', error);
        failed += webhookMissing.length;
      }
    }

    // Fix Layer 2: Remove extra WebSocket subscriptions
    if (websocketExtra.length > 0) {
      logger.log(`🔧 [Auto-Heal] Removing ${websocketExtra.length} orphaned WebSocket subscriptions...`);
      for (const sn of websocketExtra) {
        try {
          await removeWebSocketEnabledDevice(sn);
          success++;
        } catch (error) {
          logger.error(`❌ [Auto-Heal] Failed to remove WebSocket for ${sn}:`, error);
          failed++;
        }
      }
      logger.log(`✅ [Auto-Heal] Removed ${websocketExtra.length} orphaned WebSocket subscriptions`);
    }

    logger.log(`🔧 [Auto-Heal] Complete: ${success} fixed, ${failed} failed`);

  } catch (error) {
    logger.error('❌ [Auto-Heal] Auto-heal process failed:', error);
  }

  return { success, failed };
}

// ============================================================
// Comprehensive Health Check
// ============================================================

/**
 * Run comprehensive health check for all subscriptions
 * Verifies both webhook and WebSocket layers, optionally auto-heals
 *
 * @param autoHeal If true, attempts to fix issues automatically
 * @returns Detailed health check result
 */
export async function runHealthCheck(autoHeal: boolean = false): Promise<HealthCheckResult> {
  const startTime = new Date();
  logger.log('🏥 [Health Check] Starting comprehensive subscription health check...');

  try {
    // Step 1: Verify both layers
    const webhookStatus = await verifyWebhookSubscriptions();
    const websocketStatus = await verifyWebSocketSubscriptions();

    // Step 2: Get all imported devices to build health details (intelliSpace only)
    const allImportedSNs = new Set<string>();

    const allBoardDevices = await getImportedDevices('intelliSpace');
    // ✅ Fix: Use fallback pattern since migration may have removed 'sn' field
    allBoardDevices.forEach(device => {
      const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId;
      if (deviceSN) {
        allImportedSNs.add(deviceSN);
      }
    });

    // Step 3: Build health details for each device
    const details: SubscriptionHealth[] = Array.from(allImportedSNs).map(sn => {
      const webhookOK = webhookStatus.subscribed.includes(sn);
      const websocketOK = websocketStatus.subscribed.includes(sn);
      const webhookMissing = webhookStatus.missing.includes(sn);

      let overall: 'healthy' | 'warning' | 'error';
      if (webhookOK && (websocketOK || !websocketStatus.missing.includes(sn))) {
        overall = 'healthy';
      } else if (webhookMissing) {
        overall = 'error';
      } else {
        overall = 'warning';
      }

      return {
        deviceSN: sn,
        webhook: {
          subscribed: webhookOK,
          needsHealing: webhookMissing
        },
        websocket: {
          subscribed: websocketOK,
          needsHealing: websocketStatus.extra.includes(sn)
        },
        overall
      };
    });

    // Step 4: Auto-heal if requested
    let healResult = { success: 0, failed: 0 };
    if (autoHeal && (webhookStatus.missing.length > 0 || websocketStatus.extra.length > 0)) {
      logger.log('🔧 [Health Check] Auto-heal enabled, fixing issues...');
      healResult = await autoHealSubscriptions(webhookStatus.missing, websocketStatus.extra);
    }

    // Step 5: Calculate summary
    const healthy = details.filter(d => d.overall === 'healthy').length;
    const warnings = details.filter(d => d.overall === 'warning').length;
    const errors = details.filter(d => d.overall === 'error').length;

    const result: HealthCheckResult = {
      timestamp: startTime,
      totalDevices: allImportedSNs.size,
      healthy,
      warnings,
      errors,
      details,
      autoHealAttempted: autoHeal,
      autoHealSuccess: healResult.success,
      autoHealFailed: healResult.failed
    };

    // Log summary
    logger.log('🏥 [Health Check] Complete:');
    logger.log(`  Total: ${result.totalDevices} devices`);
    logger.log(`  ✅ Healthy: ${result.healthy}`);
    logger.log(`  ⚠️  Warnings: ${result.warnings}`);
    logger.log(`  ❌ Errors: ${result.errors}`);
    if (autoHeal) {
      logger.log(`  🔧 Auto-heal: ${result.autoHealSuccess} fixed, ${result.autoHealFailed} failed`);
    }

    return result;

  } catch (error) {
    logger.error('❌ [Health Check] Health check failed:', error);
    throw error;
  }
}

// ============================================================
// Quick Status Check (for UI polling)
// ============================================================

/**
 * Quick subscription status check (lightweight, for UI polling)
 * Only returns summary counts, no detailed verification
 *
 * @returns Quick status summary
 */
export async function getQuickStatus(): Promise<{
  totalImported: number;
  webhookCount: number;
  websocketCount: number;
  lastCheck: Date;
}> {
  try {
    // Get imported device count (intelliSpace only - legacy boards removed)
    const boardDevices = await getImportedDevices('intelliSpace');
    const totalImported = boardDevices.length;

    // Get subscription counts (from cache, don't verify)
    const webhookResult = await ultronTuringApi.querySubscriptions();
    const websocketDevices = await getWebSocketEnabledDevicesFromFirestore();

    return {
      totalImported,
      webhookCount: webhookResult.devices?.length || 0,
      websocketCount: websocketDevices.length,
      lastCheck: new Date()
    };

  } catch (error) {
    logger.error('Failed to get quick status:', error);
    throw error;
  }
}